import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Tooltip,
  Indicator,
  Loader
} from '@mantine/core';
import { IconUserCheck, IconUserOff } from '@tabler/icons-react';
import { apiClient } from '../../services/authService';
import { notifications } from '@mantine/notifications';
import { Text } from '@mantine/core';

interface OperatorStatusToggleProps {
  canHandover?: boolean;
}

export default function OperatorStatusToggle({ canHandover = true }: OperatorStatusToggleProps) {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        // 🔒 SECURITY: Token is sent automatically via httpOnly cookie (withCredentials: true)
        const response = await apiClient.get('/api/users/me/online-status');

        if (response.data.success) {
          setIsOnline(response.data.data.is_online);
        }
      } catch (error) {
        console.error('Error fetching online status:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    fetchStatus();
  }, []);

  const toggleStatus = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      // 🔒 SECURITY: Token is sent automatically via httpOnly cookie (withCredentials: true)
      const newStatus = !isOnline;
      const response = await apiClient.put(
        '/api/users/me/online-status',
        { is_online: newStatus }
      );

      if (response.data.success) {
        setIsOnline(newStatus);
        notifications.show({
          title: newStatus ? t('operator.statusOnline') : t('operator.statusOffline'),
          message: newStatus
            ? t('operator.statusOnlineMessage')
            : t('operator.statusOfflineMessage'),
          color: newStatus ? 'green' : 'gray',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error toggling online status:', error);
      notifications.show({
        title: t('common.error'),
        message: t('operator.statusToggleError'),
        color: 'red',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!canHandover) {
    return null;
  }

  if (isInitializing) {
    return (
      <ActionIcon
        variant="light"
        size="lg"
        radius="md"
        disabled
        style={{
          background: 'rgba(128, 128, 128, 0.05)',
          border: '1px solid rgba(128, 128, 128, 0.15)'
        }}
      >
        <Loader size={16} color="gray" />
      </ActionIcon>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Text size="xs" fw={500} c="dimmed" className="hidden sm:block uppercase tracking-wider">
        {t('operator.statusLabel')}
      </Text>
      <Tooltip
        label={isOnline
          ? t('operator.statusOnline')
          : t('operator.statusOffline')
        }
        position="bottom"
        withArrow
      >
        <Indicator
          inline
          processing={isLoading}
          color={isOnline ? 'green' : 'gray'}
          size={12}
          offset={4}
          position="bottom-end"
          disabled={isLoading}
        >
          <ActionIcon
            variant="light"
            size="lg"
            radius="md"
            onClick={toggleStatus}
            loading={isLoading}
          style={{
            transition: 'all 0.2s ease',
            background: isOnline
              ? 'rgba(34, 197, 94, 0.1)'
              : 'rgba(128, 128, 128, 0.1)',
            border: `1px solid ${isOnline
              ? 'rgba(34, 197, 94, 0.3)'
              : 'rgba(128, 128, 128, 0.3)'
            }`
          }}
          aria-label={isOnline
            ? t('operator.statusOnline')
            : t('operator.statusOffline')
          }
        >
            {isOnline ? (
              <IconUserCheck
                size={20}
                style={{ color: '#22c55e' }}
              />
            ) : (
              <IconUserOff
                size={20}
                style={{ color: '#9ca3af' }}
              />
            )}
          </ActionIcon>
        </Indicator>
      </Tooltip>
    </div>
  );
}
